import { useEffect, useState } from 'react'
import { useNls } from '../../nls/nlsProvider'
import { getTeamSeatSummary, type TeamSeatSummary } from '../../services/teamSeatsService'
import './seatsStrip.css'

export type SeatsStripProps = {
  workspaceId?: string
  activeMembers?: number
  unusedJoinCodes?: number
  limit?: number
  className?: string
}

export function SeatsStrip({
  workspaceId,
  activeMembers: propActiveMembers,
  unusedJoinCodes: propUnusedJoinCodes,
  limit: propLimit,
  className = '',
}: SeatsStripProps) {
  const { t } = useNls()
  const [data, setData] = useState<TeamSeatSummary | null>(null)

  useEffect(() => {
    if (propActiveMembers !== undefined && propLimit !== undefined) return
    if (!workspaceId) return

    let cancelled = false
    void getTeamSeatSummary({ workspaceId })
      .then((res) => {
        if (!cancelled) setData(res)
      })
      .catch(() => {
        // Ignored in preview or when summary is not loadable
      })

    return () => {
      cancelled = true
    }
  }, [workspaceId, propActiveMembers, propLimit])

  const activeMembers = propActiveMembers ?? data?.activeMembers ?? 0
  const unusedJoinCodes = propUnusedJoinCodes ?? data?.unusedJoinCodes ?? 0
  const limit = propLimit ?? data?.limit ?? 125

  return (
    <div className={`seatsStrip ${className}`.trim()} data-testid="seats-strip">
      <span className="seatsStrip__item" data-testid="seats-strip-active">
        <strong className="seatsStrip__count">{activeMembers}</strong>
        <span>{t('NLSSeatsStripActiveMembers', { count: activeMembers })}</span>
      </span>
      <span className="seatsStrip__dot" aria-hidden="true">·</span>
      <span className="seatsStrip__item" data-testid="seats-strip-codes">
        <strong className="seatsStrip__count">{unusedJoinCodes}</strong>
        <span>{t('NLSSeatsStripUnusedCodes', { count: unusedJoinCodes })}</span>
      </span>
      <span className="seatsStrip__dot" aria-hidden="true">·</span>
      <span className="seatsStrip__item" data-testid="seats-strip-limit">
        <strong className="seatsStrip__count">{limit}</strong>
        <span>{t('NLSSeatsStripSeatLimit', { limit })}</span>
      </span>
    </div>
  )
}
